import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { EDITION, isIndia } from "@/lib/edition";
import { DeviceMobile, DownloadSimple, Copy, Check, WhatsappLogo, AppleLogo, AndroidLogo, ShareNetwork, Export } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function detectPlatform() {
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
}

export default function Install() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [canInstall, setCanInstall] = useState(!!window.deferredInstallPrompt);
  const platform = detectPlatform();
  const brand = EDITION.brand;

  useEffect(() => {
    api.get(`/app/install?edition=${isIndia ? "india" : "global"}`)
      .then((r) => setData(r.data))
      .catch(() => setData(false));
    const onInstallable = () => setCanInstall(true);
    window.addEventListener("pwa-installable", onInstallable);
    return () => window.removeEventListener("pwa-installable", onInstallable);
  }, []);

  const shortUrl = data && data !== false ? data.short_url : "";
  const qrSrc = data && data !== false
    ? (data.qr_png?.startsWith("http") ? data.qr_png : `${BACKEND}${data.qr_png}`)
    : null;

  const copy = async () => {
    try { await navigator.clipboard.writeText(shortUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error("Couldn't copy"); }
  };

  const androidInstall = async () => {
    const dp = window.deferredInstallPrompt;
    if (!dp) { toast.message("Use your browser menu → \u201cInstall app\u201d / \u201cAdd to Home screen\u201d."); return; }
    dp.prompt();
    try { await dp.userChoice; } catch { /* ignore */ }
    window.deferredInstallPrompt = null;
    setCanInstall(false);
  };

  const waText = encodeURIComponent(`Install the ${brand} app on your phone — scan or tap: ${shortUrl}`);
  const waHref = `https://wa.me/?text=${waText}`;

  return (
    <main className="mx-auto max-w-4xl px-5 py-12" data-testid="install-page">
      <div className="text-center">
        <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-primary text-primary-foreground"><DeviceMobile size={28} weight="fill" /></span>
        <h1 className="mt-5 font-heading text-4xl font-medium tracking-tight sm:text-5xl">Get the {brand} app</h1>
        <p className="mx-auto mt-3 max-w-xl text-muted-foreground">Install {brand} on your phone for a full-screen, app-like experience — faster access to your alumni network, right from your home screen.</p>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* QR + link card */}
        <Card className="flex flex-col items-center border-border/60 p-7 text-center shadow-sm">
          <h2 className="font-heading text-lg font-medium">Scan to install</h2>
          <p className="mt-1 text-sm text-muted-foreground">Point your phone camera at this code.</p>
          <div className="mt-5 grid h-56 w-56 place-items-center rounded-2xl border border-border/60 bg-white p-3">
            {data === null ? <div className="h-full w-full animate-pulse rounded-xl bg-muted" data-testid="qr-loading" />
              : qrSrc ? <img src={qrSrc} alt="Install QR code" className="h-full w-full object-contain" data-testid="install-qr" />
              : <span className="text-sm text-muted-foreground">QR unavailable</span>}
          </div>
          {shortUrl && (
            <div className="mt-5 flex w-full items-center gap-2 rounded-full border border-border/60 bg-muted/40 py-1.5 pl-4 pr-1.5">
              <span className="min-w-0 flex-1 truncate text-left text-sm font-medium" data-testid="install-short-url">{shortUrl}</span>
              <Button size="sm" variant="secondary" className="shrink-0 gap-1 rounded-full" onClick={copy} data-testid="copy-short-url">
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </Button>
            </div>
          )}
          <a href={waHref} target="_blank" rel="noreferrer" className="mt-3 w-full" data-testid="whatsapp-send-link">
            <Button className="w-full gap-2 rounded-full bg-[#25D366] text-white hover:bg-[#1eb257]"><WhatsappLogo size={18} weight="fill" /> Send link to my phone</Button>
          </a>
        </Card>

        {/* Instructions card */}
        <Card className="border-border/60 p-7 shadow-sm">
          <h2 className="font-heading text-lg font-medium">Install on your device</h2>

          {(platform === "android" || platform === "desktop") && (
            <div className="mt-4">
              <Button onClick={androidInstall} className="w-full gap-2 rounded-full" data-testid="android-install-btn">
                <DownloadSimple size={18} weight="bold" /> {canInstall ? "Install app" : "Add to Home screen"}
              </Button>
              {!canInstall && <p className="mt-2 text-xs text-muted-foreground">If nothing happens, open your browser menu and choose <span className="font-medium">Install app</span> or <span className="font-medium">Add to Home screen</span>.</p>}
            </div>
          )}

          <div className="mt-6 space-y-5 text-sm">
            <div>
              <p className="flex items-center gap-2 font-semibold"><AppleLogo size={18} weight="fill" /> iPhone &amp; iPad (Safari)</p>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-muted-foreground" data-testid="ios-steps">
                <li>Open this page in <span className="font-medium">Safari</span>.</li>
                <li>Tap the <span className="inline-flex items-center gap-0.5 font-medium">Share <Export size={13} /></span> button.</li>
                <li>Choose <span className="font-medium">Add to Home Screen</span>.</li>
                <li>Tap <span className="font-medium">Add</span> — the {brand} icon appears on your home screen.</li>
              </ol>
            </div>
            <div>
              <p className="flex items-center gap-2 font-semibold"><AndroidLogo size={18} weight="fill" /> Android (Chrome)</p>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-muted-foreground" data-testid="android-steps">
                <li>Tap <span className="font-medium">Install app</span> above, or open the <span className="font-medium">⋮ menu</span>.</li>
                <li>Choose <span className="font-medium">Install app</span> / <span className="font-medium">Add to Home screen</span>.</li>
                <li>Confirm — {brand} installs like a native app.</li>
              </ol>
            </div>
          </div>

          <div className="mt-6 flex items-start gap-2 rounded-xl bg-muted/50 p-3 text-xs text-muted-foreground">
            <ShareNetwork size={16} className="mt-0.5 shrink-0" />
            <span>Tip: On a computer? Scan the QR code with your phone, or use <span className="font-medium">Send link to my phone</span> to open it on mobile and install there.</span>
          </div>
        </Card>
      </div>
    </main>
  );
}
